import { Autocomplete, TextField, Chip, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';

interface Slot {
  slotId: string;
  name: string;
  [key: string]: any;
}

interface Product {
  productId: string;
  [key: string]: any;
}

interface Menu {
  slots?: Slot[];
  [key: string]: any;
}

interface SlotSelectProps {
  value: Slot | null | Slot[];
  onChange: (slot: Slot | null | Slot[]) => void;
  product?: Product | null;
  menu?: Menu | null;
  multiple?: boolean;
}

export function SlotSelect({ 
  value, 
  onChange, 
  product,
  menu,
  multiple = false 
}: SlotSelectProps) {
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  useEffect(() => {
    setSlotsLoading(true);
    
    // If no product is provided, try to get all slots from the menu
    if (!product && menu && menu.slots && Array.isArray(menu.slots)) {
      console.log('SlotSelect - Using all slots from menu:', menu.slots.length, 'slots');
      setSlots(menu.slots);
      setSlotsLoading(false);
      return;
    }
    
    // Extract slots from the product's variants
    if (product && product.variants && Array.isArray(product.variants)) {
      // Use the first variant by default, or check if there's a selected variant in product
      const selectedVariant = product.selectedVariant || product.variants[0];
      
      if (selectedVariant && selectedVariant.slots && Array.isArray(selectedVariant.slots)) {
        // Extract slot objects from the variant's slots array
        const extractedSlots = selectedVariant.slots.map((slotItem: any) => {
          // The slot object is nested under "slot" property
          return slotItem.slot;
        }).filter(Boolean);
        
        console.log('SlotSelect - Extracted from product.variants[].slots:', extractedSlots.length, 'slots', extractedSlots.map((s: any) => s.name));
        setSlots(extractedSlots);
      } else {
        console.log('SlotSelect - No slots found in selected variant:', selectedVariant);
        setSlots([]);
      }
    } else {
      console.log('SlotSelect - No variants available in product:', product);
      setSlots([]);
    }
    
    setSlotsLoading(false);
  }, [product, menu]);

  // Always use array for value in multiple mode, and ensure value matches options
  const normalizedValue = multiple
    ? Array.isArray(value) ? value.filter(v => slots.some(s => s.slotId === v.slotId)) : value ? [value] : []
    : (value && slots.some(s => s.slotId === (value as Slot).slotId)) ? value : null;

  return (
    <Autocomplete
      options={Array.isArray(slots) ? slots : []}
      getOptionLabel={(option: Slot) => {
        if (!option) return 'No slot';
        if (!option.name && !option.slotCode) return 'Unknown';
        return `${option.name ?? 'Unknown'} (${option.slotCode ?? 'Unknown'})`;
      }}
      isOptionEqualToValue={(option, val) => {
        if (!option || !val) return false;
        return option.slotId === (val as Slot).slotId;
      }}
      renderOption={(props, option) => {
        if (!option) return null;
        const label = (!option.name && !option.slotCode)
          ? 'Unknown'
          : `${option.name ?? 'Unknown'} (${option.slotCode ?? 'Unknown'})`;
        return (
          <li {...props} key={option.slotId || `slot-${Math.random()}`}>
            {label}
          </li>
        );
      }}
      renderTags={(tagValue, getTagProps) => {
        if (!tagValue || !Array.isArray(tagValue)) return [];
        return (tagValue as Slot[]).map((option, index) => {
          if (!option) return null;
          const label = (!option.name && !option.slotCode)
            ? 'Unknown'
            : `${option.name ?? 'Unknown'} (${option.slotCode ?? 'Unknown'})`;
          return (
            <Chip
              {...getTagProps({ index })}
              key={option.slotId || `slot-tag-${index}`}
              size="small"
              label={label}
            />
          );
        });
      }}
      value={normalizedValue}
      onChange={(_, newValue) => {
        if (multiple) {
          onChange(Array.isArray(newValue) ? newValue : newValue ? [newValue] : []);
        } else {
          onChange(newValue ?? null);
        }
      }}
      multiple={multiple}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Slot"
          placeholder="Search for slots..."
          fullWidth
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {slotsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
